// Shared UI kit for the agent pro experience (cream/serif design system).
// Tokens, glyphs, and typography primitives — every agent page imports from here.

import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import "./responsive.css";

// Agent-pro tokens alias the global app tokens (defined in styles/index.css
// for both `:root` and `.dark`), so the surface flips with the theme toggle.
// Hardcoding the cream values here was the bug — it kept the agent
// experience locked to light mode regardless of the user's choice.
//
// `--ag-info-*` and `--ag-violet-*` are not aliases (the app has no such
// tokens); their dark variants live in responsive.css.
export const SHELL_TOKENS: CSSProperties = {
  // @ts-expect-error — CSS custom properties
  "--ag-bg": "var(--bg)",
  "--ag-surface": "var(--surface)",
  "--ag-surface-2": "var(--surface-2)",
  "--ag-ink": "var(--ink)",
  "--ag-ink-2": "var(--ink-2)",
  "--ag-ink-3": "var(--ink-3)",
  "--ag-ink-4": "var(--ink-4)",
  "--ag-rule": "var(--rule)",
  "--ag-rule-strong": "var(--rule-strong)",
  "--ag-accent": "var(--accent)",
  "--ag-accent-soft": "var(--accent-soft)",
  "--ag-accent-ink": "var(--accent-ink)",
  "--ag-ai-bg": "var(--ai-bg)",
  "--ag-ai-rule": "var(--ai-rule)",
  "--ag-ai-accent": "var(--ai-accent)",
  "--ag-pos": "var(--pos)",
  "--ag-pos-bg": "var(--pos-bg)",
  "--ag-warn": "var(--warn)",
  "--ag-warn-bg": "var(--warn-bg)",
  "--ag-neg": "var(--neg)",
  "--ag-neg-bg": "var(--neg-bg)",
  // --ag-info-* and --ag-violet-* inherit from :root/.dark (defined in responsive.css)
  "--ag-font-ui": "var(--font-ui)",
  "--ag-font-serif": "var(--font-serif)",
  "--ag-font-mono": "var(--font-mono)",
};

// ── Glyphs ─────────────────────────────────────────────────────────────

export type GlyphName =
  | "home"
  | "cases"
  | "building"
  | "building-pin"
  | "person-search"
  | "cal"
  | "inbox"
  | "report"
  | "contact"
  | "chart"
  | "set"
  | "plus"
  | "search"
  | "sparkle"
  | "arrowR"
  | "arrowL"
  | "pipeline"
  | "eye"
  | "handshake"
  | "pencil"
  | "palette"
  | "trash"
  | "article"
  | "send"
  | "copy";

export function NavGlyph({ name, size = 13 }: { name: GlyphName; size?: number }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none" as const,
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (name) {
    case "home":
      return <svg {...common}><path d="M3 11.5 12 4l9 7.5" /><path d="M5 10v10h14V10" /></svg>;
    case "cases":
      return <svg {...common}><rect x="3" y="6" width="18" height="14" rx="2" /><path d="M9 6V4h6v2" /><path d="M3 12h18" /></svg>;
    case "building":
      return <svg {...common}><rect x="4" y="3" width="16" height="18" rx="1.5" /><path d="M9 8h.01M14 8h.01M9 12h.01M14 12h.01M9 16h.01M14 16h.01" /><path d="M10 21v-4h4v4" /></svg>;
    case "building-pin":
      return <svg {...common}><rect x="4" y="6" width="14" height="14" rx="1.5" /><path d="M9 10h.01M14 10h.01M9 14h.01M14 14h.01" /><path d="M10 20v-3h4v3" /></svg>;
    case "person-search":
      return <svg {...common}><circle cx="10" cy="8" r="4" /><path d="M3 21a7 7 0 0 1 11.5-5.4" /><circle cx="17" cy="17" r="3" /><path d="m21 21-1.5-1.5" /></svg>;
    case "cal":
      return <svg {...common}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18" /><path d="M8 3v4M16 3v4" /></svg>;
    case "inbox":
      return <svg {...common}><path d="M3 12h6l1.5 2.5h3L15 12h6" /><path d="M5 4h14l2 8v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6z" /></svg>;
    case "report":
      return <svg {...common}><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /><path d="M8 13h8M8 17h6" /></svg>;
    case "contact":
      return <svg {...common}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>;
    case "chart":
      return <svg {...common}><path d="M4 20V4M4 20h16" /><path d="M8 16v-5M12 16V8M16 16v-3" /></svg>;
    case "set":
      return <svg {...common}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3 1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8 1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" /></svg>;
    case "plus":
      return <svg {...common}><path d="M12 5v14M5 12h14" /></svg>;
    case "search":
      return <svg {...common}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>;
    case "sparkle":
      return <svg {...common}><path d="M12 3v4M12 17v4M5 12H1M23 12h-4M5.6 5.6 8.4 8.4M15.6 15.6l2.8 2.8M5.6 18.4 8.4 15.6M15.6 8.4 18.4 5.6" /></svg>;
    case "arrowR":
      return <svg {...common}><path d="M5 12h14M13 6l6 6-6 6" /></svg>;
    case "arrowL":
      return <svg {...common}><path d="M19 12H5M11 6l-6 6 6 6" /></svg>;
    case "pipeline":
      return <svg {...common}><path d="M4 6h6M4 12h12M4 18h8" /><circle cx="12" cy="6" r="2" /><circle cx="18" cy="12" r="2" /><circle cx="14" cy="18" r="2" /></svg>;
    case "eye":
      return <svg {...common}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>;
    case "handshake":
      return <svg {...common}><path d="M3 12l4-4 4 1 4-1 4 4-2 2-3-2-3 2-3-2-3 2z" /><path d="M11 9 8 12" /></svg>;
    case "pencil":
      return <svg {...common}><path d="M16 3l5 5-12 12H4v-5L16 3z" /></svg>;
    case "palette":
      return <svg {...common}><path d="M12 3a9 9 0 1 0 4 17c1.6-.7.6-2.6-.7-3a1.5 1.5 0 0 1 .4-3H17a4 4 0 0 0 4-4 9 9 0 0 0-9-7z" /><circle cx="7.5" cy="10" r="0.9" fill="currentColor" /><circle cx="12" cy="7.5" r="0.9" fill="currentColor" /><circle cx="16" cy="10" r="0.9" fill="currentColor" /></svg>;
    case "trash":
      return <svg {...common}><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M5 6h14l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6z" /><path d="M10 11v6M14 11v6" /></svg>;
    case "article":
      return <svg {...common}><path d="M4 4h12a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4z" /><path d="M18 6h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2" /><path d="M8 9h6M8 13h6M8 17h4" /></svg>;
    case "send":
      return <svg {...common}><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" /></svg>;
    case "copy":
      return <svg {...common}><rect x="9" y="9" width="12" height="12" rx="2" /><rect x="3" y="3" width="12" height="12" rx="2" /></svg>;
  }
}

export function BrandGlyph({ size = 26 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 26 26">
      <rect width="26" height="26" rx="6" fill="var(--ag-ai-bg)" stroke="var(--ag-ai-rule)" strokeWidth="1" />
      <path d="M10 6 H6 a2 2 0 0 0 -2 2 V18 a2 2 0 0 0 2 2 H10" fill="none" stroke="var(--ag-ink)" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M16 6 H20 a2 2 0 0 1 2 2 V18 a2 2 0 0 1 -2 2 H16" fill="none" stroke="var(--ag-accent)" strokeWidth="2.2" strokeLinecap="round" />
      <circle cx="13" cy="13" r="1.5" fill="var(--ag-ai-accent)" />
    </svg>
  );
}

// ── Tokens map for status pills ────────────────────────────────────────

// Stage / type badge tones reference the agent tokens so they flip with
// dark mode along with the rest of the surface. Exact mapping per design:
//   New / Dormant   → warn  (warm yellow)
//   Active / Won    → pos   (green)
//   Viewing         → info  (blue)
//   Offer / Closing → violet
//   Closed Lost     → neg   (red)
export const STAGE_TONE: Record<string, { bg: string; ink: string }> = {
  New: { bg: "var(--ag-warn-bg)", ink: "var(--ag-warn)" },
  Active: { bg: "var(--ag-accent-soft)", ink: "var(--ag-accent)" },
  Viewing: { bg: "var(--ag-info-bg)", ink: "var(--ag-info)" },
  "Offer / Negotiation": { bg: "var(--ag-violet-bg)", ink: "var(--ag-violet)" },
  Closing: { bg: "var(--ag-violet-bg)", ink: "var(--ag-violet)" },
  Dormant: { bg: "var(--ag-warn-bg)", ink: "var(--ag-warn)" },
  "Closed Won": { bg: "var(--ag-pos-bg)", ink: "var(--ag-pos)" },
  "Closed Lost": { bg: "var(--ag-neg-bg)", ink: "var(--ag-neg)" },
};

export const TYPE_BADGE: Record<"property" | "client", { label: string; icon: GlyphName; bg: string; ink: string }> = {
  property: { label: "Property", icon: "building-pin", bg: "var(--ag-accent-soft)", ink: "var(--ag-accent-ink)" },
  client: { label: "Client", icon: "person-search", bg: "var(--ag-ai-bg)", ink: "var(--ag-ai-accent)" },
};

// ── Primitives ─────────────────────────────────────────────────────────

export function Pill({
  children,
  bg,
  ink,
  mono,
}: {
  children: ReactNode;
  bg?: string;
  ink?: string;
  mono?: boolean;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 9px",
        borderRadius: 999,
        background: bg || "var(--ag-surface-2)",
        color: ink || "var(--ag-ink-2)",
        fontSize: 11.5,
        fontWeight: 600,
        letterSpacing: 0.1,
        fontFamily: mono ? "var(--ag-font-mono)" : "inherit",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

// Stage labels are stored as canonical English strings on the wire
// (the schema enum is English). Render-time we translate via i18n
// so zh users see "看房中" instead of "Viewing".
const STAGE_I18N_KEY: Record<string, string> = {
  "New": "agent.stage.new",
  "Active": "agent.stage.active",
  "Viewing": "agent.stage.viewing",
  "Offer / Negotiation": "agent.stage.offer",
  "Closing": "agent.stage.closing",
  "Dormant": "agent.stage.dormant",
  "Closed Won": "agent.stage.closedWon",
  "Closed Lost": "agent.stage.closedLost",
};

export function StagePill({ stage }: { stage: string }) {
  const tone = STAGE_TONE[stage] ?? STAGE_TONE.Active;
  const { t } = useTranslation();
  const key = STAGE_I18N_KEY[stage];
  const label = key ? (t(key, { defaultValue: stage }) as string) : stage;
  return (
    <Pill bg={tone.bg} ink={tone.ink}>
      {label}
    </Pill>
  );
}

export function TypeBadge({ type, compact }: { type: "property" | "client"; compact?: boolean }) {
  const tb = TYPE_BADGE[type];
  const { t } = useTranslation();
  const label = t(`agent.type.${type}`, { defaultValue: tb.label }) as string;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: compact ? "2px 7px 2px 5px" : "3px 9px 3px 6px",
        borderRadius: 999,
        background: tb.bg,
        color: tb.ink,
        fontSize: compact ? 10.5 : 11.5,
        fontWeight: 600,
        letterSpacing: 0.2,
      }}
    >
      <NavGlyph name={tb.icon} size={compact ? 11 : 12} />
      {label}
    </span>
  );
}

export function UrgencyDot({ urgency }: { urgency?: "high" | "medium" | "low" }) {
  const c = urgency === "high" ? "#bf3838" : urgency === "medium" ? "#c98c00" : "rgba(23,32,29,0.25)";
  return (
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: 999,
        background: c,
        display: "inline-block",
        flex: "none",
      }}
    />
  );
}

export function Avatar({ initials, size = 28, tint }: { initials: string; size?: number; tint?: string }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        background: tint || "#dbd2bd",
        color: "var(--ag-accent-ink)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.4,
        fontWeight: 700,
        letterSpacing: 0.5,
        flex: "none",
      }}
    >
      {initials}
    </div>
  );
}

export function Card({
  children,
  padding = 16,
  style = {},
}: {
  children: ReactNode;
  padding?: number | string;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        background: "var(--ag-surface)",
        border: "1px solid var(--ag-rule)",
        borderRadius: 14,
        padding,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function AIBlock({
  children,
  label = "AI note",
  tone = "serif",
}: {
  children: ReactNode;
  label?: string;
  tone?: "serif" | "ui";
}) {
  return (
    <div
      style={{
        background: "var(--ag-ai-bg)",
        border: "1px solid var(--ag-ai-rule)",
        borderRadius: 12,
        padding: "12px 14px",
        display: "flex",
        gap: 10,
      }}
    >
      <div
        style={{
          width: 20,
          height: 20,
          borderRadius: 999,
          background: "var(--ag-ai-accent)",
          color: "var(--ag-ai-bg)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
          marginTop: 1,
        }}
      >
        <NavGlyph name="sparkle" size={11} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: "uppercase",
            color: "var(--ag-ai-accent)",
            marginBottom: 3,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontFamily: tone === "serif" ? "var(--ag-font-serif)" : "var(--ag-font-ui)",
            fontStyle: tone === "serif" ? "italic" : "normal",
            fontSize: tone === "serif" ? 14 : 13,
            color: "var(--ag-ai-accent)",
            lineHeight: 1.45,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export function SectionTitle({
  children,
  hint,
  action,
}: {
  children: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 14 }}>
      <h2
        style={{
          margin: 0,
          fontFamily: "var(--ag-font-serif)",
          fontWeight: 500,
          fontSize: 17,
          letterSpacing: -0.2,
          color: "var(--ag-ink)",
        }}
      >
        {children}
      </h2>
      {hint && <span style={{ fontSize: 12, color: "var(--ag-ink-3)" }}>{hint}</span>}
      <span style={{ flex: 1 }} />
      {action}
    </div>
  );
}

export function Big({
  n,
  label,
  highlight,
}: {
  n: ReactNode;
  label: string;
  highlight?: boolean;
}) {
  return (
    <div>
      <div
        style={{
          fontFamily: "var(--ag-font-serif)",
          fontWeight: 500,
          fontSize: 22,
          letterSpacing: -0.3,
          color: highlight ? "var(--ag-accent)" : "var(--ag-ink)",
          lineHeight: 1.1,
        }}
      >
        {n}
      </div>
      <div
        style={{
          fontSize: 11,
          color: "var(--ag-ink-3)",
          marginTop: 3,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          fontWeight: 600,
        }}
      >
        {label}
      </div>
    </div>
  );
}

export function Stat({ n, label, highlight }: { n: ReactNode; label: string; highlight?: boolean }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          fontFamily: "var(--ag-font-mono)",
          fontWeight: 500,
          fontSize: 18,
          color: highlight ? "var(--ag-accent)" : "var(--ag-ink)",
          lineHeight: 1,
        }}
      >
        {n}
      </div>
      <div
        style={{
          fontSize: 10.5,
          color: "var(--ag-ink-3)",
          marginTop: 3,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          fontWeight: 600,
        }}
      >
        {label}
      </div>
    </div>
  );
}

export function ActionBtn({
  icon,
  label,
  primary,
  onClick,
}: {
  icon: GlyphName;
  label: string;
  primary?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "8px 12px",
        borderRadius: 8,
        background: primary ? "var(--ag-accent)" : "var(--ag-surface)",
        color: primary ? "var(--ag-surface)" : "var(--ag-ink-2)",
        border: `1px solid ${primary ? "var(--ag-accent)" : "var(--ag-rule-strong)"}`,
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontFamily: "var(--ag-font-ui)",
      }}
    >
      <NavGlyph name={icon} size={13} /> {label}
    </button>
  );
}

// ── EmptyState ─────────────────────────────────────────────────────────
//
// Use this anywhere a list / card grid renders zero rows. Replacing a
// bare "0" or a silent gap with a labelled empty state + one-click CTA
// is the difference between "I think the page is broken" and "I see
// what this page is for and how to fill it". Every agent-side page
// surfaces multiple lists (cases, leads, viewings, offers, etc.) so
// this is a high-leverage primitive — keep it in ui.tsx, not inlined.
export function EmptyState({
  icon = "sparkle",
  title,
  hint,
  ctaLabel,
  onCta,
  compact,
}: {
  icon?: GlyphName;
  title: string;
  hint?: string;
  ctaLabel?: string;
  onCta?: () => void;
  // compact: tighter padding for in-card empty rows (e.g. Today's
  // schedule, dormant) vs the full-bleed page-level empty state.
  compact?: boolean;
}) {
  const pad = compact ? "20px 18px" : "36px 22px";
  return (
    <div
      style={{
        padding: pad,
        textAlign: "center",
        background: "transparent",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 999,
          background: "var(--ag-surface-2, var(--ag-surface))",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--ag-ink-3)",
        }}
      >
        <NavGlyph name={icon} size={16} />
      </div>
      <div
        style={{
          fontFamily: "var(--ag-font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: compact ? 13.5 : 15,
          color: "var(--ag-ink-2)",
        }}
      >
        {title}
      </div>
      {hint && (
        <div
          style={{
            fontSize: 12,
            color: "var(--ag-ink-3)",
            maxWidth: 340,
            lineHeight: 1.5,
          }}
        >
          {hint}
        </div>
      )}
      {ctaLabel && onCta && (
        <button
          onClick={onCta}
          style={{
            marginTop: 4,
            padding: "7px 14px",
            borderRadius: 8,
            border: "1px solid var(--ag-accent)",
            background: "var(--ag-accent)",
            color: "var(--ag-bg, white)",
            fontSize: 12.5,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "var(--ag-font-ui)",
          }}
        >
          {ctaLabel}
        </button>
      )}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

/** Format dollar amounts in $1.45M / $850K shorthand. */
export function formatM(value: number | null | undefined, prefix = "$"): string {
  if (value == null) return "—";
  if (value >= 1_000_000) {
    const m = value / 1_000_000;
    return `${prefix}${m.toFixed(m % 1 === 0 ? 0 : 2)}M`;
  }
  return `${prefix}${Math.round(value / 1000)}K`;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
